import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { notification } from "../Common/Store/CommonSignals";

const initialState = {
  roles: [],
  status: "idle",
  error: null,
};

export const getRoles = createAsyncThunk("roles/getRoles", async () => {
  const response = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/Role`);
  return response.data;
});

export const deleteRole = createAsyncThunk("roles/deleteRole", async ({ roleId, userName }) => {
  try {
    await axios.delete(
      `${process.env.REACT_APP_API_ENDPOINT}/api/role/${roleId}?userId=${userName}`
    );
    notification.value = {
      show: true,
      type: "success",
      message: "Role deleted successfully!",
    };
    return roleId;
  } catch (error) {
    notification.value = {
      show: true,
      type: "error",
      message: "Failed to delete role!",
      error: error,
    };
    throw error;
  }
});

export const createRole = createAsyncThunk("roles/createRole", async ({ roleData, userName }) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_ENDPOINT}/api/Role?userId=${userName}`,
      roleData
    );
    notification.value = {
      show: true,
      type: "success",
      message: "Role created successfully!",
    };
    return response.data;
  } catch (error) {
    notification.value = {
      show: true,
      type: "error",
      message: "Failed to create role!",
      error: error,
    };
    throw error;
  }
});

export const updateRole = createAsyncThunk("roles/updateRole", async ({ roleData, userName }) => {
  try {
    await axios.put(
      `${process.env.REACT_APP_API_ENDPOINT}/api/Role/${roleData.id}?userId=${userName}`,
      roleData
    );
    notification.value = {
      show: true,
      type: "success",
      message: "Role updated successfully!",
    };
    return roleData;
  } catch (error) {
    notification.value = {
      show: true,
      type: "error",
      message: "Failed to update role!",
      error: error,
    };
    throw error;
  }
});

const loadingStates = {
  IDLE: "idle",
  GETTING: "getting",
  SAVING: "saving",
  DELETING: "deleting",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
};

export const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    resetRolesStatus: (state) => {
      state.status = loadingStates.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRoles.pending, (state) => {
      state.status = loadingStates.GETTING;
      state.error = null;
    });
    builder.addCase(getRoles.fulfilled, (state, action) => {
      state.status = loadingStates.SUCCEEDED;
      state.roles = action.payload;
    });
    builder.addCase(getRoles.rejected, (state, action) => {
      state.status = loadingStates.FAILED;
      state.error = action.error.message;
    });
    builder.addCase(deleteRole.pending, (state) => {
      state.status = loadingStates.DELETING;
      state.error = null;
    });
    builder.addCase(deleteRole.rejected, (state, action) => {
      state.status = loadingStates.FAILED;
      state.error = action.error.message;
    });
    builder.addCase(deleteRole.fulfilled, (state, action) => {
      state.status = loadingStates.SUCCEEDED;
      state.roles = state.roles.filter((role) => role.id !== action.payload);
    });
    builder.addCase(createRole.pending, (state) => {
      state.status = loadingStates.SAVING;
      state.error = null;
    });
    builder.addCase(createRole.rejected, (state, action) => {
      state.status = loadingStates.FAILED;
      state.error = action.error.message;
    });
    builder.addCase(createRole.fulfilled, (state, action) => {
      state.status = loadingStates.SUCCEEDED;
      state.roles.push(action.payload);
    });
    builder.addCase(updateRole.pending, (state) => {
      state.status = loadingStates.SAVING;
      state.error = null;
    });
    builder.addCase(updateRole.rejected, (state, action) => {
      state.status = loadingStates.FAILED;
      state.error = action.error.message;
    });
    builder.addCase(updateRole.fulfilled, (state, action) => {
      state.status = loadingStates.SUCCEEDED;
      const updatedRole = action.payload;
      const existingRoleIndex = state.roles.findIndex((role) => role.id === updatedRole.id);
      if (existingRoleIndex >= 0) {
        state.roles[existingRoleIndex] = updatedRole;
      }
    });
  },
});

export const { resetRolesStatus } = rolesSlice.actions;

export default rolesSlice.reducer;

export const selectAllRoles = (state) => state.roles.roles;

export const selectRoleById = (state, roleId) =>
  state.roles.roles.find((role) => role.id === roleId);
